/**
 * QuickDetailModal - Lightweight detail view modals for the Operations Dashboard
 *
 * Shows essential information without navigating away from the dashboard.
 * Provides quick actions and a "View Full Details" option.
 */

import { useEffect, useRef } from 'react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Card, CardContent } from '../ui/Card';
import { ActionBar } from '../actions';
import type { WorkOrder, Client, ServiceLog } from '../../lib/api';

// =============================================================================
// MODAL WRAPPER
// =============================================================================

interface ModalWrapperProps {
  title: string;
  subtitle?: string;
  badges?: Array<{ label: string; variant: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'earth' }>;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

function ModalWrapper({ title, subtitle, badges, onClose, children, footer }: ModalWrapperProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Focus trap - focus the modal when it opens
  useEffect(() => {
    modalRef.current?.focus();
  }, []);

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        className="bg-sage-50 w-full sm:w-[520px] sm:rounded-2xl sm:max-h-[85vh] max-h-[95vh] overflow-hidden flex flex-col animate-slide-up outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-forest-800 text-white px-4 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              {badges && badges.length > 0 && (
                <div className="flex items-center gap-2 mb-1">
                  {badges.map((badge, i) => (
                    <Badge key={i} variant={badge.variant} size="sm">
                      {badge.label}
                    </Badge>
                  ))}
                </div>
              )}
              <h2 id="modal-title" className="text-lg font-semibold truncate">{title}</h2>
              {subtitle && (
                <p className="text-forest-300 text-sm truncate">{subtitle}</p>
              )}
            </div>
            <button
              onClick={onClose}
              aria-label="Close modal"
              className="w-8 h-8 rounded-full bg-forest-700/50 flex items-center justify-center hover:bg-forest-700 transition-colors ml-3"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="border-t border-sage-200 bg-white px-4 py-3 flex-shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// INFO ROW COMPONENT
// =============================================================================

interface InfoRowProps {
  label: string;
  value: React.ReactNode;
  className?: string;
}

function InfoRow({ label, value, className = '' }: InfoRowProps) {
  if (!value) return null;
  return (
    <div className={`flex items-start gap-3 ${className}`}>
      <span className="text-sage-500 text-sm w-24 flex-shrink-0">{label}</span>
      <span className="text-forest-900 text-sm flex-1">{value}</span>
    </div>
  );
}

// =============================================================================
// WORK ORDER QUICK DETAIL
// =============================================================================

const WORK_ORDER_STATUSES: Record<string, { label: string; variant: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'earth' }> = {
  '1': { label: 'Pending', variant: 'warning' },
  '2': { label: 'In Progress', variant: 'info' },
  '3': { label: 'Assigned', variant: 'info' },
  '4': { label: 'Ordered', variant: 'default' },
  '5': { label: 'Complete', variant: 'success' },
  '6': { label: 'Ready', variant: 'success' },
  '7': { label: 'Declined', variant: 'danger' },
};

function getStatusInfo(status: string) {
  const stage = status.charAt(0);
  return WORK_ORDER_STATUSES[stage] || { label: status, variant: 'default' as const };
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    timeZone: 'America/Los_Angeles',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

interface WorkOrderQuickDetailProps {
  workOrder: WorkOrder;
  onClose: () => void;
  onViewFull: () => void;
}

export function WorkOrderQuickDetail({
  workOrder,
  onClose,
  onViewFull,
}: WorkOrderQuickDetailProps) {
  const wo = workOrder;
  const statusInfo = getStatusInfo(wo.status);

  const badges = [
    { label: `#${wo.id}`, variant: 'default' as const },
    statusInfo,
    ...(wo.urgent ? [{ label: 'Urgent', variant: 'earth' as const }] : []),
  ];

  return (
    <ModalWrapper
      title={wo.clientName || 'Unknown Client'}
      subtitle={wo.itemName || 'No item specified'}
      badges={badges}
      onClose={onClose}
      footer={
        <div className="flex items-center justify-between gap-3">
          <Button variant="secondary" onClick={onViewFull}>
            View Full Details →
          </Button>
          <ActionBar
            table="workorders"
            row={wo as unknown as Record<string, unknown>}
            context="detail"
          />
        </div>
      }
    >
      {/* Item Details */}
      <Card>
        <CardContent className="py-3 space-y-2">
          <h3 className="text-sm font-semibold text-forest-900 mb-2">Order Details</h3>
          <InfoRow label="Item" value={wo.itemName} />
          <InfoRow label="Quantity" value={wo.qty > 1 ? `${wo.qty}x` : null} />
          <InfoRow label="Location" value={wo.location} />
          <InfoRow label="Created" value={formatDate(wo.timestamp)} />
          <InfoRow label="Assigned To" value={wo.assignedToName} />
          {wo.wholesaleCostEstimate && (
            <InfoRow label="Est. Cost" value={`$${wo.wholesaleCostEstimate.toLocaleString()}`} />
          )}
        </CardContent>
      </Card>

      {/* Notes */}
      {wo.notes && (
        <Card>
          <CardContent className="py-3">
            <h3 className="text-sm font-semibold text-forest-900 mb-2">Notes</h3>
            <p className="text-sm text-sage-700 whitespace-pre-wrap">{wo.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Images (thumbnails) */}
      {(wo.imageUrl || wo.imageUrl2 || wo.imageUrl3 || wo.imageUrl4) && (
        <Card>
          <CardContent className="py-3">
            <h3 className="text-sm font-semibold text-forest-900 mb-2">Photos</h3>
            <div className="flex gap-2 overflow-x-auto">
              {[wo.imageUrl, wo.imageUrl2, wo.imageUrl3, wo.imageUrl4]
                .filter((url): url is string => Boolean(url))
                .map((url, i) => (
                  <img
                    key={i}
                    src={url}
                    alt={`Photo ${i + 1}`}
                    className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                  />
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </ModalWrapper>
  );
}

// =============================================================================
// CLIENT QUICK DETAIL
// =============================================================================

interface ClientQuickDetailProps {
  client: Client;
  onClose: () => void;
  onViewFull: () => void;
}

export function ClientQuickDetail({
  client,
  onClose,
  onViewFull,
}: ClientQuickDetailProps) {
  const badges: Array<{ label: string; variant: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'earth' }> = [
    { label: client.isInactive ? 'Inactive' : 'Active', variant: client.isInactive ? 'warning' : 'success' },
    ...(client.areaCode ? [{ label: client.areaCode, variant: 'default' as const }] : []),
  ];

  const formatAddress = () => {
    const parts = [];
    if (client.address?.address1) parts.push(client.address.address1);
    if (client.address?.city) parts.push(client.address.city);
    return parts.join(', ') || '-';
  };

  return (
    <ModalWrapper
      title={client.clientName || 'Unknown Client'}
      subtitle={client.businessName ?? undefined}
      badges={badges}
      onClose={onClose}
      footer={
        <div className="flex items-center justify-between gap-3">
          <Button variant="secondary" onClick={onViewFull}>
            View Full Details →
          </Button>
          <ActionBar
            table="clients"
            row={client as unknown as Record<string, unknown>}
            context="detail"
          />
        </div>
      }
    >
      {/* Contact Info */}
      <Card>
        <CardContent className="py-3 space-y-2">
          <h3 className="text-sm font-semibold text-forest-900 mb-2">Contact</h3>
          <InfoRow label="Address" value={formatAddress()} />
          <InfoRow label="Phone" value={client.contactPhone} />
          <InfoRow label="Email" value={client.contactEmail} />
        </CardContent>
      </Card>

      {/* Service Info */}
      <Card>
        <CardContent className="py-3 space-y-2">
          <h3 className="text-sm font-semibold text-forest-900 mb-2">Service</h3>
          <InfoRow label="Tech ID" value={client.techId} />
          <InfoRow label="Route ID" value={client.routeId} />
          <InfoRow label="Interval" value={client.intervalDays ? `${client.intervalDays} days` : null} />
        </CardContent>
      </Card>

      {/* Access Notes */}
      {client.accessNotes && (
        <Card>
          <CardContent className="py-3">
            <h3 className="text-sm font-semibold text-forest-900 mb-2">Access Notes</h3>
            <p className="text-sm text-sage-700 line-clamp-3">{client.accessNotes}</p>
          </CardContent>
        </Card>
      )}
    </ModalWrapper>
  );
}

// =============================================================================
// SERVICE LOG QUICK DETAIL
// =============================================================================

interface ServiceLogQuickDetailProps {
  serviceLog: ServiceLog;
  onClose: () => void;
  onViewFull: () => void;
}

export function ServiceLogQuickDetail({
  serviceLog,
  onClose,
  onViewFull,
}: ServiceLogQuickDetailProps) {
  const log = serviceLog;

  const badges: Array<{ label: string; variant: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'earth' }> = [
    ...(log.missedService ? [{ label: 'Missed', variant: 'danger' as const }] : [{ label: 'Completed', variant: 'success' as const }]),
  ];

  const formatDateTime = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <ModalWrapper
      title={log.clientName || 'Unknown Client'}
      subtitle={`Service by ${log.employeeName || 'Unknown'}`}
      badges={badges}
      onClose={onClose}
      footer={
        <div className="flex items-center justify-between gap-3">
          <Button variant="secondary" onClick={onViewFull}>
            View Full Details →
          </Button>
        </div>
      }
    >
      {/* Service Details */}
      <Card>
        <CardContent className="py-3 space-y-2">
          <h3 className="text-sm font-semibold text-forest-900 mb-2">Service Details</h3>
          <InfoRow label="Time" value={formatDateTime(log.timestamp)} />
          <InfoRow label="Employee" value={log.employeeName} />
          <InfoRow label="Time Log" value={log.timeLog} />
          <InfoRow label="Visit Log" value={log.visitLog} />
        </CardContent>
      </Card>

      {/* Service Notes */}
      {log.serviceNotes && (
        <Card>
          <CardContent className="py-3">
            <h3 className="text-sm font-semibold text-forest-900 mb-2">Service Notes</h3>
            <p className="text-sm text-sage-700 whitespace-pre-wrap">{log.serviceNotes}</p>
          </CardContent>
        </Card>
      )}

      {/* Missed Service Info */}
      {log.missedService && (
        <Card>
          <CardContent className="py-3">
            <h3 className="text-sm font-semibold text-forest-900 mb-2">Missed Service</h3>
            <p className="text-sm text-sage-700">{log.missedService}</p>
          </CardContent>
        </Card>
      )}
    </ModalWrapper>
  );
}
